
/**
 * Hook to access dealer data embedded in the HTML by the server
 * 
 * This hook extracts dealer data that was embedded directly in the HTML
 * during server-side rendering, providing immediate access to the data
 * without requiring additional API calls.
 */

import { useEffect, useState } from 'react';

interface EmbeddedDealerData {
  dealers: any[];
  cities: string[];
  dealerDetails: Record<string, any>;
}

export function useEmbeddedDealerData() {
  const [dealerData, setDealerData] = useState<EmbeddedDealerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log("🔍 useEmbeddedDealerData: Starting search for embedded data");
    
    // Skip in SSR context
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.log("🚫 useEmbeddedDealerData: SSR context, skipping");
      setIsLoading(false);
      return;
    }
    
    try {
      // Try to find the embedded dealer data script
      const scriptElement = document.getElementById('__DEALER_DATA__');
      console.log("📜 useEmbeddedDealerData: Script element found:", !!scriptElement);
      
      if (scriptElement && scriptElement.textContent) {
        // Parse the JSON data from the script
        const parsedData = JSON.parse(scriptElement.textContent);
        setDealerData(parsedData);
        console.log('✅ useEmbeddedDealerData: Loaded dealer data from script element:', {
          dealersCount: parsedData.dealers?.length || 0,
          citiesCount: parsedData.cities?.length || 0,
          dealerDetailsCount: Object.keys(parsedData.dealerDetails || {}).length
        });
      } else {
        // Fallback to window.__INITIAL_DEALER_DATA__ if script element not found
        const windowData = (window as any).__INITIAL_DEALER_DATA__;
        const windowDealers = (window as any).__SERVER_DEALERS__ || [];
        const windowCities = (window as any).__SERVER_CITIES__ || [];
        
        console.log('🔄 useEmbeddedDealerData: Fallback to window data:', { 
          hasWindowData: !!windowData, 
          dealersCount: windowDealers.length,
          citiesCount: windowCities.length
        });
        
        if (windowData || windowDealers.length > 0) {
          const fallbackData = {
            dealers: windowDealers,
            cities: windowCities,
            dealerDetails: windowData || {}
          };
          setDealerData(fallbackData);
          console.log('✅ useEmbeddedDealerData: Set fallback data:', {
            dealersCount: fallbackData.dealers.length,
            citiesCount: fallbackData.cities.length
          });
        } else {
          // No embedded data found
          console.log('❌ useEmbeddedDealerData: No embedded dealer data found in HTML');
          setDealerData(null);
        }
      }
    } catch (err) {
      console.error('💥 useEmbeddedDealerData: Error parsing embedded dealer data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error parsing dealer data'));
      setDealerData(null);
    } finally {
      console.log("🏁 useEmbeddedDealerData: Setting loading to false");
      setIsLoading(false);
    }
  }, []);

  return { dealerData, isLoading, error };
}

/**
 * Hook to access a specific dealer's data from the embedded HTML
 */
export function useEmbeddedDealerDetail(dealerId: string | undefined) {
  const [dealer, setDealer] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!dealerId) {
      setIsLoading(false);
      return;
    }

    // Skip in SSR context
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      setIsLoading(false);
      return;
    }

    try {
      // Try to find the embedded dealer data script
      const scriptElement = document.getElementById('__DEALER_DATA__');
      
      if (scriptElement && scriptElement.textContent) {
        // Parse the JSON data from the script
        const parsedData = JSON.parse(scriptElement.textContent);
        const dealerDetail = parsedData.dealerDetails[dealerId];
        
        if (dealerDetail) {
          console.log(`Found dealer ${dealerId} in script element:`, dealerDetail);
          setDealer(dealerDetail);
        } else {
          // Dealer not found in embedded data
          console.log(`Dealer ${dealerId} not found in script element data`);
          setDealer(null);
        }
      } else {
        // Fallback to window.__INITIAL_DEALER_DATA__ if script element not found
        const windowData = (window as any).__INITIAL_DEALER_DATA__;
        console.log(`Checking window.__INITIAL_DEALER_DATA__ for dealer ${dealerId}:`, 
          windowData ? `Found data with ${Object.keys(windowData).length} dealers` : 'No data found');
        
        if (windowData && windowData[dealerId]) {
          setDealer(windowData[dealerId]);
        } else {
          // Dealer not found in window data
          setDealer(null);
        }
      }
    } catch (err) {
      console.error(`Error parsing embedded dealer data for ID ${dealerId}:`, err);
      setError(err instanceof Error ? err : new Error(`Unknown error parsing dealer data for ID ${dealerId}`));
      setDealer(null);
    } finally {
      setIsLoading(false);
    }
  }, [dealerId]);

  return { dealer, isLoading, error };
}
